import {
  CostingMethod,
  CostingMethodEnum,
  ItemMargin,
  MarginTypeEnum,
} from "@forgepoint/shared";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MarginInputRow } from "./MarginInputRow";

interface ItemMarginEditorProps {
  costingMethod: CostingMethod;
  itemId?: string;
  margins: ItemMargin[];
  enableItemMargins: boolean;
  onMarginsChange: (margins: ItemMargin[]) => void;
  onEnableItemMarginsChange: (enabled: boolean) => void;
  switchId?: string;
}

/**
 * Component that manages item cost margins for UNIT_COST items.
 * Handles default margins and quantity-based margin overrides.
 * @param props - Component props including costingMethod, margins, and handlers
 * @returns Margin editor UI for UNIT_COST items, or null for BOM items
 */
export function ItemMarginEditor({
  costingMethod,
  itemId: _itemId,
  margins,
  enableItemMargins,
  onMarginsChange,
  onEnableItemMarginsChange,
  switchId = "enable-item-margins",
}: ItemMarginEditorProps) {
  const handleAddMargin = () => {
    const maxQty = Math.max(
      0,
      ...margins
        .map((m) => m.minQty)
        .filter((q): q is number => q !== undefined),
    );

    const newMargin: ItemMargin = {
      type: MarginTypeEnum.all,
      minQty: maxQty + 10,
      marginAmount: { type: "PERCENTAGE", percentage: "0" },
    };
    onMarginsChange([...margins, newMargin]);
  };

  const handleRemoveMargin = (index: number) => {
    onMarginsChange(margins.filter((_, i) => i !== index));
  };

  const handleMarginChange = (
    index: number,
    field: "minQty" | "marginType" | "value",
    value: string | number,
  ) => {
    const updatedMargins = [...margins];

    if (field === "minQty") {
      const numValue =
        typeof value === "number" ? value : parseInt(value as string, 10);
      updatedMargins[index] = {
        ...updatedMargins[index],
        minQty: isNaN(numValue) ? 1 : numValue,
      };
    } else if (field === "marginType") {
      const marginType = value as "PERCENTAGE" | "AMOUNT";
      updatedMargins[index] = {
        ...updatedMargins[index],
        marginAmount:
          marginType === "PERCENTAGE"
            ? { type: "PERCENTAGE", percentage: "0" }
            : { type: "AMOUNT", amount: "0.00" },
      };
    } else {
      const currentMargin = updatedMargins[index];
      if (currentMargin.marginAmount.type === "PERCENTAGE") {
        updatedMargins[index] = {
          ...currentMargin,
          marginAmount: {
            type: "PERCENTAGE",
            percentage: value.toString(),
          },
        };
      } else {
        updatedMargins[index] = {
          ...currentMargin,
          marginAmount: {
            type: "AMOUNT",
            amount: value.toString(),
          },
        };
      }
    }

    onMarginsChange(updatedMargins);
  };

  const handleDefaultMarginChange = (
    field: "marginType" | "value",
    value: string | number,
  ) => {
    const defaultMargin = margins.find((m) => m.minQty === undefined);
    const overrides = margins.filter((m) => m.minQty !== undefined);

    const stringValue = value.toString();

    let updatedDefault: ItemMargin;
    if (field === "marginType") {
      const marginType = stringValue as "PERCENTAGE" | "AMOUNT";
      updatedDefault = {
        type: MarginTypeEnum.all,
        marginAmount:
          marginType === "PERCENTAGE"
            ? { type: "PERCENTAGE", percentage: "0" }
            : { type: "AMOUNT", amount: "0.00" },
      };
    } else {
      if (!defaultMargin) {
        return;
      }

      if (defaultMargin.marginAmount.type === "PERCENTAGE") {
        updatedDefault = {
          ...defaultMargin,
          marginAmount: {
            type: "PERCENTAGE",
            percentage: stringValue,
          },
        };
      } else {
        updatedDefault = {
          ...defaultMargin,
          marginAmount: {
            type: "AMOUNT",
            amount: stringValue,
          },
        };
      }
    }

    onMarginsChange([updatedDefault, ...overrides]);
  };

  const handleToggleItemMargins = (checked: boolean) => {
    onEnableItemMarginsChange(checked);
    if (!checked) {
      onMarginsChange([]);
    } else if (margins.length === 0) {
      const newMargin: ItemMargin = {
        type: MarginTypeEnum.all,
        marginAmount: { type: "PERCENTAGE", percentage: "0" },
      };
      onMarginsChange([newMargin]);
    }
  };

  if (costingMethod === CostingMethodEnum.BOM) {
    return null;
  }

  const defaultMargin = margins.find((m) => m.minQty === undefined);
  const overrides = margins.filter((m) => m.minQty !== undefined);

  return (
    <div className="divide-y">
      <div className="py-2.5">
        <div className="flex items-center justify-between gap-4">
          <Label
            htmlFor={switchId}
            className="text-sm font-medium leading-tight min-w-0 cursor-pointer"
          >
            Item cost margin
          </Label>
          <div className="w-[200px] shrink-0 flex justify-end">
            <Switch
              id={switchId}
              checked={enableItemMargins}
              onCheckedChange={handleToggleItemMargins}
            />
          </div>
        </div>
      </div>

      {enableItemMargins && (
        <div className="border rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold">Item Cost Margin</h3>

          {defaultMargin && (
            <MarginInputRow
              isDefault={true}
              marginType={defaultMargin.marginAmount.type}
              value={
                defaultMargin.marginAmount.type === "PERCENTAGE"
                  ? defaultMargin.marginAmount.percentage
                  : defaultMargin.marginAmount.amount
              }
              onMarginTypeChange={(type) =>
                handleDefaultMarginChange("marginType", type)
              }
              onValueChange={(value) =>
                handleDefaultMarginChange("value", value)
              }
            />
          )}

          {overrides.length > 0 && (
            <div className="space-y-2 pl-4 border-l-2 border-border">
              <span className="text-xs font-medium text-muted-foreground">
                Quantity Overrides
              </span>
              {overrides.map((override) => {
                const actualIndex = margins.findIndex((m) => m === override);
                return (
                  <MarginInputRow
                    key={actualIndex}
                    isDefault={false}
                    minQty={override.minQty}
                    marginType={override.marginAmount.type}
                    value={
                      override.marginAmount.type === "PERCENTAGE"
                        ? override.marginAmount.percentage
                        : override.marginAmount.amount
                    }
                    onMinQtyChange={(qty) =>
                      handleMarginChange(actualIndex, "minQty", qty)
                    }
                    onMarginTypeChange={(type) =>
                      handleMarginChange(actualIndex, "marginType", type)
                    }
                    onValueChange={(value) =>
                      handleMarginChange(actualIndex, "value", value)
                    }
                    onRemove={() => handleRemoveMargin(actualIndex)}
                    showQuantityInput={true}
                  />
                );
              })}
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddMargin}
            className="w-full"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Quantity Override
          </Button>
        </div>
      )}
    </div>
  );
}
